
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Loader2 } from 'lucide-react';
import { supabase } from '../src/libs/supabase';
import { sendEmail } from '../src/libs/sendEmail';

interface LeadFormModalProps {
    isOpen: boolean;
    onClose: () => void;
    sector: string;
}

export const LeadFormModal: React.FC<LeadFormModalProps> = ({ isOpen, onClose, sector }) => {
    const { t, i18n } = useTranslation();
    const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success'>('idle');
    const [formData, setFormData] = useState({ name: '', company: '', email: '' });

    if (!isOpen) return null;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setFormStatus('submitting');

        try {
            // 1. Insert into Database
            const { error } = await supabase.from('leads').insert([{
                company: formData.company,
                email: formData.email,
                sector: sector,
                details: `Name: ${formData.name}`
            }]);

            if (error) throw error;

            // 2. Send Email Notification (Non-blocking, with language)
            sendEmail({
                email: formData.email,
                name: formData.name, // Use the name from the form
                subject: `New Lead: ${sector} Audit`,
                message: `New request from ${formData.name} (${formData.email}) for ${sector} Audit.\nCompany/Domain: ${formData.company}`,
                lang: i18n.language
            }).catch(err => console.error('Failed to send email:', err));

            setFormStatus('success');
            setFormData({ name: '', company: '', email: '' });
        } catch (err) {
            console.error('Error submitting lead:', err);
            alert(t('lead_modal.error'));
            setFormStatus('idle');
        }
    };

    return (
        <div className="fixed inset-0 bg-black/90 z-[100] flex items-center justify-center p-6 animate-fade-in backdrop-blur-sm">
            <div className="bg-white w-full max-w-xl shadow-2xl relative overflow-hidden p-12 rounded-xl border border-gray-100">
                <button onClick={onClose} className="absolute top-6 right-6 text-gray-400 hover:text-black transition-colors"><X size={24} /></button>

                {formStatus === 'success' ? (
                    <div className="text-center py-12">
                        <div className="w-20 h-20 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-6">
                            <Loader2 className="w-10 h-10 animate-pulse" /> {/* Replaced checkmark with something available, though checkmark is better if imported */}
                            <svg className="w-10 h-10" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                        </div>
                        <h3 className="text-3xl font-black mb-4">{t('lead_modal.success_title')}</h3>
                        <p className="text-gray-600 mb-8">{t('lead_modal.success_msg')}</p>
                        <button
                            onClick={onClose}
                            className="px-8 py-3 bg-[#0B1121] text-white rounded-lg font-bold hover:bg-black transition-all"
                        >
                            {t('shared.website.modal.success.close') || "Close"}
                        </button>
                    </div>
                ) : (
                    <>
                        <div className="mb-10">
                            <div className="flex items-center gap-4 mb-4">
                                <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center text-clever-teal">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path><polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline><line x1="12" y1="22.08" x2="12" y2="12"></line></svg>
                                </div>
                                <h3 className="text-3xl font-bold text-gray-900 tracking-tighter">{sector} {t('lead_modal.title')}</h3>
                            </div>
                            <p className="text-gray-500 text-lg">
                                {t('lead_modal.desc') || "We analyze your status quo and verify potential automations."}
                            </p>
                        </div>

                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div>
                                <label className="block text-xs font-black uppercase tracking-widest text-gray-400 mb-2">Name</label>
                                <input
                                    required
                                    className="w-full px-6 py-4 bg-gray-50 border-2 border-transparent focus:border-clever-teal focus:bg-white outline-none transition-all font-bold"
                                    placeholder={t('lead_modal.name_placeholder') || "Max Mustermann"}
                                    value={formData.name}
                                    onChange={e => setFormData({ ...formData, name: e.target.value })}
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-black uppercase tracking-widest text-gray-400 mb-2">{t('lead_modal.company_label') || "Company / Domain"}</label>
                                <input
                                    required
                                    className="w-full px-6 py-4 bg-gray-50 border-2 border-transparent focus:border-clever-teal focus:bg-white outline-none transition-all font-bold"
                                    placeholder={t('lead_modal.company_placeholder')}
                                    value={formData.company}
                                    onChange={e => setFormData({ ...formData, company: e.target.value })}
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-black uppercase tracking-widest text-gray-400 mb-2">Email</label>
                                <input
                                    required
                                    type="email"
                                    className="w-full px-6 py-4 bg-gray-50 border-2 border-transparent focus:border-clever-teal focus:bg-white outline-none transition-all font-bold"
                                    placeholder={t('lead_modal.email_placeholder')}
                                    value={formData.email}
                                    onChange={e => setFormData({ ...formData, email: e.target.value })}
                                />
                            </div>
                            <button
                                type="submit"
                                disabled={formStatus === 'submitting'}
                                className="w-full bg-[#0B1121] text-white py-6 font-bold uppercase tracking-widest hover:bg-gray-900 transition-all flex items-center justify-center gap-3 disabled:opacity-70 rounded-sm shadow-xl hover:shadow-2xl mt-8"
                            >
                                {formStatus === 'submitting' ? <><Loader2 className="animate-spin" size={20} /> {t('lead_modal.submitting')}</> : <>{t('lead_modal.submit')} <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"></line><polyline points="12 5 19 12 12 19"></polyline></svg></>}
                            </button>
                        </form>
                    </>
                )}
            </div>
        </div>
    );
};
